import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  FileText,
  Plus,
  Search,
  Filter,
  Eye,
  Edit,
  Send,
  CheckCircle2,
  Clock,
  XCircle,
  AlertCircle,
  Users,
  Calendar,
  Building,
  TrendingUp,
  MoreVertical,
  Download,
  Mail,
  Loader2,
  X
} from "lucide-react";
import { formatDateYearString } from "../../utils/formatageDate";
import axios from "../../axios/AxiosInstance";
import { ClientContractRow } from "../ClientContractRow";

export default function ContratPage() {
  const [contrats, setContrats] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("all");
  const [currentTab, setCurrentTab] = useState("all");
  const [viewMode, setViewMode] = useState("table");
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [pdfModal, setPdfModal] = useState({ open: false, url: null, title: "" });

  useEffect(() => {
    setIsLoading(true);
    axios
      .get("pld-contract/freelance")
      .then((res) => {
        setContrats(res.data);
      })
      .catch((err) => console.error(err))
      .finally(() => setIsLoading(false));
  }, []);

  const handleView = async (contrat) => {
    const id = contrat._id || contrat.id;
    setActionLoading({ id, type: "view" });
    try {
      const res = await axios.get(`pld-contract/view/${id}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfModal({
        open: true,
        url,
        title: `Contrat ${contrat.NumeroContrat}`,
      });
    } catch (err) {
      console.error("Erreur lors de la visualisation:", err);
      alert("Impossible de charger le contrat. Le PDF n'est peut-être pas encore disponible.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleDownload = async (contrat) => {
    const id = contrat._id || contrat.id;
    setActionLoading({ id, type: "download" });
    try {
      const res = await axios.get(`pld-contract/download/${id}`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `contrat_${contrat.NumeroContrat}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Erreur lors du téléchargement:", err);
      alert("Impossible de télécharger le contrat. Le PDF n'est peut-être pas encore disponible.");
    } finally {
      setActionLoading(null);
    }
  };

  const closePdfModal = () => {
    if (pdfModal.url) {
      URL.revokeObjectURL(pdfModal.url);
    }
    setPdfModal({ open: false, url: null, title: "" });
  };

  const filteredContrats = contrats.filter((c) => {
    if (currentTab === "enCours" && c.statut !== "en cours") return false;
    if (currentTab === "termines" && c.statut !== "terminé") return false;
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (searchType === "all" &&
        (c.RaisonSociale?.toLowerCase().includes(term) ||
          c.NumeroContrat.includes(term))) ||
      (searchType === "client" &&
        c.RaisonSociale?.toLowerCase().includes(term)) ||
      (searchType === "numero" && c.NumeroContrat.includes(term))
    );
  });

  // Calcul des statistiques
  const stats = {
    total: contrats.length,
    enCours: contrats.filter(c => c.statut === "en cours").length,
    termines: contrats.filter(c => c.statut === "terminé").length,
    enAttente: contrats.filter(c => c.statut === "en attente").length
  };

  // Fonction pour obtenir l'icône et la couleur du statut
  const getStatusIcon = (status) => {
    switch (status) {
      case 'en cours':
        return <Clock className="w-4 h-4 text-blue-600" />;
      case 'terminé':
        return <CheckCircle2 className="w-4 h-4 text-green-600" />;
      case 'en attente':
        return <AlertCircle className="w-4 h-4 text-orange-500" />;
      default:
        return <XCircle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'en cours':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'terminé':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'en attente':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="w-full min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="w-full p-8">
        {/* Loading Overlay */}
        <AnimatePresence>
          {isLoading && (
            <motion.div
              className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <motion.div
                className="bg-white rounded-xl p-6 flex items-center space-x-3"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
              >
                <Loader2 className="animate-spin h-6 w-6 text-blue-600" />
                <span className="text-slate-700">Chargement des contrats...</span>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center">
                <FileText className="w-8 h-8 mr-3 text-blue-600" />
                Gestion des Contrats
              </h1>
              <p className="text-lg text-slate-600">
                Gerez vos contrats de placements et de missions
              </p>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-blue-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl hover:bg-blue-700 transition-all duration-200 flex items-center space-x-2"
            >
              <Plus className="w-5 h-5" />
              <span>Nouveau contrat</span>
            </motion.button>
          </div>
        </motion.div>

        {/* Statistics Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Total contrats</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">En cours</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">{stats.enCours}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-lg">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">Terminés</p>
                <p className="text-3xl font-bold text-green-600 mt-1">{stats.termines}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-lg">
                <CheckCircle2 className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </motion.div>

          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 hover:shadow-md transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-600 text-sm font-medium">En attente</p>
                <p className="text-3xl font-bold text-orange-500 mt-1">{stats.enAttente}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-lg">
                <AlertCircle className="w-6 h-6 text-orange-500" />
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Filters and Search */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2">
              {[
                { id: "all", label: "Tous", count: stats.total },
                { id: "enCours", label: "En cours", count: stats.enCours },
                { id: "termines", label: "Terminés", count: stats.termines }
              ].map((tab) => (
                <motion.button
                  key={tab.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setCurrentTab(tab.id)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                    currentTab === tab.id
                      ? "bg-slate-800 text-white shadow-md"
                      : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                  }`}
                >
                  <span>{tab.label}</span>
                  <span className={`px-2 py-1 rounded-full text-xs ${
                    currentTab === tab.id
                      ? "bg-white/20 text-white"
                      : "bg-slate-200 text-slate-600"
                  }`}>
                    {tab.count}
                  </span>
                </motion.button>
              ))}
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher un contrat..."
                  className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200 w-full sm:w-64"
                />
              </div>

              <div className="relative">
                <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                <select
                  value={searchType}
                  onChange={(e) => setSearchType(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all duration-200 appearance-none bg-white"
                >
                  <option value="all">Tous les champs</option>
                  <option value="client">Client</option>
                  <option value="numero">N° Contrat</option>
                </select>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Contracts Table */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden"
        >
          {filteredContrats.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Aucun contrat trouvé</h3>
              <p className="text-slate-500">
                {searchTerm ? "Essayez de modifier vos critères de recherche" : "Commencez par créer votre premier contrat"}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      N° de contrat
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Intérimaire
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Période
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Statut
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  <AnimatePresence>
                    {filteredContrats.map((contrat, index) => (
                      <motion.tr
                        key={contrat.id || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        className="hover:bg-slate-50 transition-colors duration-200"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="bg-slate-100 p-2 rounded-lg mr-3">
                              <FileText className="w-4 h-4 text-slate-600" />
                            </div>
                            <div className="text-sm font-medium text-slate-900">
                              {contrat.NumeroContrat}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Building className="w-4 h-4 text-slate-400 mr-2" />
                            <div className="text-sm text-slate-900">{contrat.RaisonSociale}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Users className="w-4 h-4 text-slate-400 mr-2" />
                            <div className="text-sm text-slate-900">
                              {contrat.NomPrenom || contrat.interimaire || "Non assigné"}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-slate-400 mr-2" />
                            <div className="text-sm text-slate-900">
                              {formatDateYearString(contrat.DateDebut)} - {formatDateYearString(contrat.DateFin)}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(contrat.statut)}`}>
                            {getStatusIcon(contrat.statut)}
                            <span className="ml-1 capitalize">{contrat.statut}</span>
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex items-center justify-center space-x-2">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleView(contrat)}
                              disabled={actionLoading?.id === (contrat._id || contrat.id) && actionLoading?.type === "view"}
                              className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 disabled:opacity-50"
                              title="Voir le contrat"
                            >
                              {actionLoading?.id === (contrat._id || contrat.id) && actionLoading?.type === "view" ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Eye className="w-4 h-4" />
                              )}
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDownload(contrat)}
                              disabled={actionLoading?.id === (contrat._id || contrat.id) && actionLoading?.type === "download"}
                              className="p-2 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all duration-200 disabled:opacity-50"
                              title="Télécharger"
                            >
                              {actionLoading?.id === (contrat._id || contrat.id) && actionLoading?.type === "download" ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Download className="w-4 h-4" />
                              )}
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="p-2 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded-lg transition-all duration-200"
                              title="Renvoyer"
                            >
                              <Mail className="w-4 h-4" />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-all duration-200"
                              title="Plus d'actions"
                            >
                              <MoreVertical className="w-4 h-4" />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>
          )}
        </motion.div>

        {/* Pagination */}
        {filteredContrats.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 flex items-center justify-between"
          >
            <div className="text-sm text-slate-600">
              Affichage de <span className="font-medium">{filteredContrats.length}</span> contrat{filteredContrats.length > 1 ? 's' : ''} sur <span className="font-medium">{contrats.length}</span>
            </div>
            <div className="flex items-center space-x-2">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200"
              >
                Précédent
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-2 text-sm font-medium text-white bg-slate-800 border border-slate-800 rounded-lg hover:bg-slate-700 transition-colors duration-200"
              >
                1
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-3 py-2 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors duration-200"
              >
                Suivant
              </motion.button>
            </div>
          </motion.div>
        )}
        {/* Modal de visualisation du PDF */}
        <AnimatePresence>
          {pdfModal.open && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closePdfModal}
            >
              <motion.div
                className="bg-white w-[90vw] max-w-[1100px] h-[85vh] rounded-xl shadow-2xl flex flex-col overflow-hidden"
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
                  <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    {pdfModal.title}
                  </h2>
                  <button
                    onClick={closePdfModal}
                    className="p-2 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <div className="flex-1 p-2">
                  <iframe
                    src={pdfModal.url}
                    className="w-full h-full rounded-lg"
                    title={pdfModal.title}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
